import { wordAttributes } from './wordUtils';
import { applySpiranteMutations } from './spiranteMutations';
import { applySoftMutations } from './softMutations';
import { applyHardMutations } from './hardMutations';
import { applyMixedMutations } from './mixedMutations';

export const mutationTypes = {
  SOFT: 'SOFT',
  SOFT_INCOMPLETE: 'SOFT_INCOMPLETE',
  SOFT_CONDITIONAL: 'SOFT_CONDITIONAL',
  HARD: 'HARD',
  MIXED: 'MIXED',
  SPIRANTE: 'SPIRANTE',
  INCOMPLETE: 'INCOMPLETE',
};

export const lettersForMutations_idx = {
  'k': 'k',
  'p': 'p',
  't': 't',
  'g': 'g',
  'gw': 'gw',
  'b': 'b',
  'd': 'd',
  'm': 'm',
};

export const lettersForMutations = Object.values(lettersForMutations_idx);

export const possessive_headers = {
  'ma': { label: 'ma', mutationType: mutationTypes.SPIRANTE },
  'da': { label: 'da', mutationType: mutationTypes.SOFT },
  'e': { label: 'e', mutationType: mutationTypes.SOFT },
  'he': { label: 'he', mutationType: mutationTypes.SPIRANTE },
  'hon': { label: 'hon', mutationType: mutationTypes.SPIRANTE },
  'ho': { label: 'ho', mutationType: mutationTypes.HARD },
  'o': { label: 'o', mutationType: mutationTypes.SPIRANTE },
};

export const table_possessive_headers = Object.values(possessive_headers);

const en_possessive_headers_patches = {
  'ma': { label: 'em' },
  'da': { label: 'ez', mutationType: mutationTypes.HARD },
};

export const table_en_possessive_headers = Object.values(possessive_headers).map(possessive_header => {
  if (en_possessive_headers_patches[ possessive_header.label ]) {
    return { ...possessive_header, ...en_possessive_headers_patches[ possessive_header.label ] };

  } else {
    return { ...possessive_header, label: `en ${possessive_header.label}` };
  }
});

const da_possessive_headers_patches = {
  'ma': { label: 'd\'am' },
  'da': { label: 'd\'az', mutationType: mutationTypes.HARD },
};

export const table_da_possessive_headers = Object.values(possessive_headers).map(possessive_header => {
  if (da_possessive_headers_patches[ possessive_header.label ]) {
    return { ...possessive_header, ...da_possessive_headers_patches[ possessive_header.label ] };

  } else {
    return { ...possessive_header, label: `d'${possessive_header.label}` };
  }
});

export const table_number_headers_idx = {
  'daou/div': { label: 'daou/div', label_m: 'daou', label_f: 'div', mutationType: mutationTypes.SOFT },
  'tri/teir': { label: 'tri/teir', label_m: 'tri', label_f: 'teir', mutationType: mutationTypes.SPIRANTE },
  'pevar/peder': { label: 'pevar/peder', label_m: 'pevar', label_f: 'peder', mutationType: mutationTypes.SPIRANTE },
  'pemp': { label: 'pemp', mutationType: null },
  'c\'hwech': { label: 'c\'hwech', mutationType: null },
  'seizh': { label: 'seizh', mutationType: null },
  'eizh': { label: 'eizh', mutationType: null },
  'nav': { label: 'nav', mutationType: mutationTypes.SPIRANTE },
};

export const table_number_headers = Object.values(table_number_headers_idx);

export function getAttributeForMutation (word) {

  const sourceContent = word.sourceContent ? word.sourceContent.trim() : '';
  const resultContent = word.resultContent ? word.resultContent.trim() : '';

  if (!sourceContent && resultContent) {
    return wordAttributes.resultContent;

  } else if (!resultContent && sourceContent) {
    return wordAttributes.sourceContent;

  } else {
    return word.refForMutation;
  }
}

export function updateWord (word, mutateFn, demutateFn) {

  const attributeForMutation = getAttributeForMutation(word);
  if (attributeForMutation === wordAttributes.sourceContent) {
    word.refForMutation = wordAttributes.sourceContent;
    mutateFn(word);

  } else if (attributeForMutation === wordAttributes.resultContent) {
    word.refForMutation = wordAttributes.resultContent;
    demutateFn(word);

  } else {
    throw Error(`Unknown ref ${attributeForMutation}`);
  }

}

export function mutate (word, mutations, mutationCause) {
  const source = word.sourceContent;

  let mutatedText = null;

  if (mutations[ 'gw' ]) {
    if (source.startsWith('gw')) {
      mutatedText = mutations[ 'gw' ] + source.substring('gw'.length);
    } else if (source.startsWith('GW')) {
      mutatedText = mutations[ 'GW' ] + source.substring('GW'.length);
    } else if (source.startsWith('Gw')) {
      mutatedText = mutations[ 'Gw' ] + source.substring('Gw'.length);
    }
  }

  if (!mutatedText) {
    const mutation = mutations[ source[ 0 ] ];
    if (mutation) {
      mutatedText = mutation + source.substring(1);
    }
  }

  if (mutatedText) {
    word.resultContent = mutatedText;
    word.mutationCause = mutationCause;
  }
}

export function demutate (word, reverseMutations, mutationCause) {
  const result = word.resultContent;

  let demutatedText = null;

  if (reverseMutations[ 'v' ]) {
    if (result.startsWith('v')) {
      demutatedText = '[' + reverseMutations[ 'v' ].join('/') + ']' + result.substring(1);
    } else if (result.startsWith('V')) {
      demutatedText = '[' + reverseMutations[ 'V' ].join('/') + ']' + result.substring(1);
    }
  }

  if (reverseMutations[ 'c\'h' ]) {
    if (result.startsWith('c\'h')) {
      demutatedText = reverseMutations[ 'c\'h' ] + result.substring('c\'h'.length);
    } else if (result.startsWith('C\'H')) {
      demutatedText = reverseMutations[ 'C\'H' ] + result.substring('C\'H'.length);
    } else if (result.startsWith('C\'h')) {
      demutatedText = reverseMutations[ 'C\'h' ] + result.substring('C\'h'.length);
    }

  }

  if (reverseMutations[ 'kw' ]) {
    if (result.startsWith('kw')) {
      demutatedText = reverseMutations[ 'kw' ] + result.substring('kw'.length);
    } else if (result.startsWith('KW')) {
      demutatedText = reverseMutations[ 'KW' ] + result.substring('KW'.length);
    } else if (result.startsWith('Kw')) {
      demutatedText = reverseMutations[ 'Kw' ] + result.substring('Kw'.length);
    }
  }

  if (!demutatedText) {
    const reverseMutation = reverseMutations[ result[ 0 ] ];
    if (reverseMutation) {
      demutatedText = reverseMutation + result.substring(1);
    }
  }

  if (demutatedText) {
    word.sourceContent = demutatedText;
    word.mutationCause = mutationCause;
  }
}

const type2fn = {
  [ mutationTypes.SPIRANTE ]: applySpiranteMutations,
  [ mutationTypes.SOFT ]: applySoftMutations,
  [ mutationTypes.HARD ]: applyHardMutations,
  [ mutationTypes.MIXED ]: applyMixedMutations,
};

export const mutateWord = (word, mutationType) => {
  if (mutationType) {
    return type2fn[ mutationType ](0, [ word ]);
  } else {
    return word;
  }
};

export function getRowHeaderLabel (rowHeader, word) {
  if ('label_f' in rowHeader) {
    if (word.isFeminine()) {
      return rowHeader.label_f;
    } else {
      return rowHeader.label_m;
    }
  } else {
    return rowHeader.label;
  }
}

export const mutateWordForRow = (word, rowHeader) => {
  const copyWord = word.copy();
  mutateWord(copyWord, rowHeader.mutationType);

  const hasMutated = !!copyWord.mutationCause;
  const result = copyWord.resultContent || copyWord.sourceContent;

  return {
    copyWord,
    hasMutated,
    result,
  };
};

export const buildMutationRows = (rowHeaders, word) => {

  const hasMutations = {
    soft: false,
    hard: false,
    mixed: false,
    spirante: false,
  };

  const rows = rowHeaders.map((rowHeader) => {

    const { copyWord, hasMutated, result } = mutateWordForRow(word, rowHeader);

    if (hasMutated) {
      if (copyWord.mutationCause === mutationTypes.SOFT) {
        hasMutations.soft = true;
      } else if (copyWord.mutationCause === mutationTypes.HARD) {
        hasMutations.hard = true;
      } else if (copyWord.mutationCause === mutationTypes.MIXED) {
        hasMutations.mixed = true;
      } else if (copyWord.mutationCause === mutationTypes.SPIRANTE) {
        hasMutations.spirante = true;
      }
    }
    const label = getRowHeaderLabel(rowHeader, copyWord);

    return {
      rowHeader,
      hasMutated,
      label,
      result,
    };

  });

  return {
    rows,
    hasMutations,
  };
};
